import { FilterOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { Row, Col, DatePicker, Form } from 'antd';
import TableComponent from 'components/Table/TableComponent';
import FormFilter from 'components/Filter/FormFilter';
import { InputForm, SelectForm, Button } from 'components';
import Content from 'layouts/Content';
import DatePickerForm from 'components/DatePicker/DatePickerForm';
import ButtonFilter from 'components/Button/ButtonFilter';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import DebtApis from 'apis/DebtApis';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { PlantApi } from 'apis/PlantApis';
import { DefaultOptionType } from 'antd/es/select';
import dayjs from 'dayjs';
import formatNumber from 'utils/constants/functions';

export default function DebtLimitPage() {
  const { setTitle, setBreadcrumb } = useTitle();
  const [data, setData] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [openFilter, setOpenFilter] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });
  const [pageSize, setPageSize] = useState(10);
  // Dropdown chi nhánh lấy từ API
  const [branchOptions, setBranchOptions] = useState<DefaultOptionType[]>([]);
  const [loadingBranch, setLoadingBranch] = useState(false);
  useEffect(() => {
    setLoadingBranch(true);
    PlantApi.getAllPlantFilterHO()
      .then((res: any) => {
        const items = res?.data?.getPlant?.items || [];
        // Loại bỏ 5000 (hội sở)
        const filtered = items.filter((item: any) => item.id !== '5000');
        setBranchOptions(
          filtered.map((item: any) => ({
            label: item.id + (item.name ? ` - ${item.name}` : ''),
            value: item.id,
          })),
        );
        setLoadingBranch(false);
      })
      .catch(() => setLoadingBranch(false));
  }, []);

  const columns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 60,
      align: 'center' as const,
      render: (_: any, __: any, idx: number) =>
        (pageInfo.currentPage - 1) * pageSize + idx + 1,
    },
    // { title: 'Mã Id', dataIndex: 'id', width: 90, align: 'center' as const },
    {
      title: 'Mã KH',
      dataIndex: 'sapCustomerId',
      width: 110,
      align: 'center' as const,
    },
    {
      title: 'Tên KH',
      dataIndex: 'storeName',
      width: 180,
      align: 'center' as const,
    },
    {
      title: 'Loại',
      dataIndex: 'typeName',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Mã chi nhánh',
      dataIndex: 'plantId',
      width: 90,
      align: 'center' as const,
    },
    {
      title: 'Mã cha',
      dataIndex: 'mainCustomerId',
      width: 110,
      align: 'center' as const,
    },
    {
      title: 'Tên KH cha',
      dataIndex: 'mainCustomerName',
      width: 180,
      align: 'center' as const,
    },
    {
      title: 'Hạn mức / lần xuất bán',
      align: 'center' as const,

      children: [
        {
          title: 'Q10',
          dataIndex: 'goldThreadSalesLimit',
          width: 130,
          align: 'center' as const,
        },
        {
          title: 'VND',
          dataIndex: 'amountSalesLimit',
          width: 130,
          align: 'center' as const,
          render: (v: any) => formatNumber(v),
        },
      ],
    },
    {
      title: 'Hạn mức nợ Q10 Hàng truyền thống',
      align: 'center' as const,
      children: [
        {
          title: 'Q10',
          dataIndex: 'goldThreadQ10Traditional',
          width: 130,
          align: 'center' as const,
        },
        {
          title: 'Ngày',
          dataIndex: 'dateQ10Traditional',
          width: 130,
          align: 'center' as const,
        },
      ],
    },
    {
      title: 'Hạn mức nợ Tiền công Hàng truyền thống',
      align: 'center' as const,
      children: [
        {
          title: 'VND',
          dataIndex: 'amountWagesTraditional',
          width: 130,
          align: 'center' as const,
          render: (v: any) => formatNumber(v),
        },
        {
          title: 'Ngày',
          dataIndex: 'dateWagesTraditional',
          width: 130,
          align: 'center' as const,
        },
      ],
    },
    {
      title: 'Hạn mức nợ Hàng Cao cấp',
      align: 'center' as const,
      children: [
        {
          title: 'VND',
          dataIndex: 'amountHighendLimit',
          width: 130,
          align: 'center' as const,
          render: (v: any) => formatNumber(v),
        },
        {
          title: 'Ngày',
          dataIndex: 'dateHighendLimit',
          width: 130,
          align: 'center' as const,
        },
      ],
    },
    {
      title: 'Hạn mức nợ Hàng Cao cấp và Tiền công Hàng truyền thống',
      align: 'center' as const,
      children: [
        {
          title: 'VND',
          dataIndex: 'amountHighendTraditionalLimit',
          width: 10,
          align: 'center' as const,
          render: (v: any) => formatNumber(v),
        },
        {
          title: 'Ngày',
          dataIndex: 'dateHighendTraditionalLimit',
          width: 130,
          align: 'center' as const,
        },
      ],
    },
    {
      title: 'Đơn vị tiền',
      dataIndex: 'currency',
      // width: 200,
      align: 'center' as const,
    },
    {
      title: 'Đơn vị Q10',
      dataIndex: 'q10Unit',
      // width: 250,
      align: 'center' as const,
    },
    {
      title: 'Người khai báo',
      dataIndex: 'declareUser',
      // width: 350,
      align: 'center' as const,
    },
    {
      title: 'Thời gian hiệu lực từ',
      dataIndex: 'effectiveTimeFrom',
      // width: 400,
      align: 'center' as const,
    },
    {
      title: 'Thời gian hiệu lực đến',
      dataIndex: 'effectiveTimeTo',
      // width: 300,
      align: 'center' as const,
    },
    {
      title: 'Người cập nhật',
      dataIndex: 'updateUser',
      // width: 200,
      align: 'center' as const,
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'updateDate',
      // width: 200,
      align: 'center' as const,
      render: (v: string) => {
        if (!v) return '';
        const date = dayjs(v.trim());
        return date.isValid() ? date.format('DD/MM/YYYY') : v;
      },
    },
  ];

  useEffect(() => {
    setTitle('Hạn mức công nợ');
    setBreadcrumb([{ title: 'Kế toán/Thủ quỹ' }, { title: 'Hạn mức công nợ' }]);
    // Gọi API luôn khi vào trang (load mặc định)
    handleSearch({});
    // eslint-disable-next-line
  }, []);

  const handleSearch = async (values: any) => {
    setLoading(true);
    try {
      const input: any = {};
      if (values.type) input.type = values.type;
      if (values.customer) input.sapCustomerId = values.customer;
      if (values.parentCustomer) input.parentCustomerId = values.parentCustomer;
      if (values.fromDate) input.effectiveTimeFrom = values.fromDate;
      if (values.toDate) input.effectiveTimeTo = values.toDate;
      const apiRes = await DebtApis.getRuleCustomerDebitList({
        input,
        skip: ((values.page || 1) - 1) * (values.pageSize || pageSize),
        take: values.pageSize || pageSize,
      });
      const items = apiRes?.data?.getRuleCustomerDebitList?.items || [];
      setData(
        items.map((item: any, idx: number) =>
          mapApiData(
            item,
            ((values.page || 1) - 1) * (values.pageSize || pageSize) + idx,
          ),
        ),
      );
      setPageInfo({
        currentPage: values.page || 1,
        itemsPerPage: values.pageSize || pageSize,
        totalItems:
          apiRes?.data?.getRuleCustomerDebitList?.pageInfo?.totalItems ||
          items.length,
      });
    } catch (err) {
      setData([]);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    form.resetFields();
    handleSearch({ page: 1, pageSize });
  };

  const handleTableChange = (pagination: any) => {
    setPageInfo((prev) => ({
      ...prev,
      currentPage: pagination.current,
      itemsPerPage: pagination.pageSize,
    }));
    setPageSize(pagination.pageSize);
    handleSearch({
      ...form.getFieldsValue(),
      page: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const input: any = {};
      const values = form.getFieldsValue();
      if (values.branch) input.plantId = values.branch;
      if (values.customer) input.sapCustomerId = values.customer;
      if (values.parentCustomer) input.parentCustomerId = values.parentCustomer;
      if (values.fromDate) input.effectiveTimeFrom = values.fromDate;
      if (values.toDate) input.effectiveTimeTo = values.toDate;
      const apiRes = await DebtApis.getRuleCustomerDebitList({
        input,
        skip: 0,
        take: pageInfo.totalItems || 1000,
      });
      const items = apiRes?.data?.getRuleCustomerDebitList?.items || [];
      const exportData = items.map((item: any, idx: number) =>
        mapApiData(item, idx),
      );
      exportExcel_v2(columns, exportData, 'HanMucCongNo');
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { label: 'Khách hàng', value: 'RCD01' },
    { label: 'Chi nhánh', value: 'RCD02' },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div></div>
        <div className="flex gap-2">
          <ButtonFilter isOpen={openFilter} setIsOpen={setOpenFilter} />
          <ButtonExport onClick={handleExportExcel} />
        </div>
      </div>
      {openFilter && (
        <Content>
          <FormFilter
            form={form}
            onFinish={handleSearch}
            onCancel={handleCancel}
            customClass="mb-0"
          >
            <Row gutter={[16, 0]}>
              <Col span={5}>
                {/* <Form.Item name="branch" label="Chi nhánh">
                  <SelectForm
                    options={branchOptions}
                    loading={loadingBranch}
                    placeholder="Chọn chi nhánh"
                    allowClear
                  />
                </Form.Item> */}
                <Form.Item name="type" label="Loại">
                  <SelectForm
                    options={typeOptions}
                    placeholder="Chọn loại"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name="customer" label="Khách hàng">
                  <InputForm
                    placeholder="Tìm khách hàng"
                    allowClear
                    suffix={<span className="anticon anticon-search" />}
                  />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item name="parentCustomer" label="Khách hàng cha">
                  <InputForm
                    placeholder="Tìm khách hàng cha"
                    allowClear
                    suffix={<span className="anticon anticon-search" />}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="fromDate" label="Thời gian hiệu lực từ">
                  <DatePickerForm
                    placeholder="dd/mm/yyyy"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="toDate" label="Thời gian hiệu lực đến">
                  <DatePickerForm
                    placeholder="dd/mm/yyyy"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormFilter>
        </Content>
      )}
      <Content>
        <TableComponent
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            current: pageInfo.currentPage,
            pageSize: pageSize,
            total: pageInfo.totalItems,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (page, pageSize) =>
              handleTableChange({ current: page, pageSize }),
          }}
          bordered
          rowKey="id"
          scroll={{ x: 'max-content' }}
        />
      </Content>
    </>
  );
}

// mapping data từ API sang table
function mapApiData(item: any, idx: number) {
  return {
    stt: idx + 1,
    id: item.id,
    sapCustomerId: item.sapCustomerId || item.tcustomer?.sapCustomerId || '',
    storeName: item.tcustomer?.storeName || '',
    typeName: item.ttype?.name || '',
    plantId: item.plantId,
    mainCustomerId: item.tcustomer?.mainCustomer?.sapCustomerId || '',
    mainCustomerName: '', // Nếu API trả về tên KH cha thì map vào đây
    goldThreadSalesLimit: item.goldThreadSalesLimit,
    amountSalesLimit: item.amountSalesLimit,
    goldThreadQ10Traditional: item.goldThreadQ10Traditional,
    dateQ10Traditional: item.dateQ10Traditional,
    amountWagesTraditional: item.amountWagesTraditional,
    dateWagesTraditional: item.dateWagesTraditional,
    amountHighendLimit: item.amountHighendLimit,
    dateHighendLimit: item.dateHighendLimit,
    amountHighendTraditionalLimit: item.amountHighendTraditionalLimit,
    dateHighendTraditionalLimit: item.dateHighendTraditionalLimit,
    currency: item.currency || 'VND',
    q10Unit: item.q10Unit || 'Chỉ',
    declareUser: item.tcreateUser?.fullName || '',
    effectiveTimeFrom: item.effectiveTimeFrom,
    effectiveTimeTo: item.effectiveTimeTo,
    updateUser: item.tmodifyUser?.fullName || '',
    updateDate: item.modifyDate
      ? dayjs(item.modifyDate.trim()).isValid()
        ? dayjs(item.modifyDate.trim()).format('DD/MM/YYYY')
        : item.modifyDate
      : '',
  };
}
